import React, { useState } from 'react';
import { Box, Grid, Button, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  RestartAlt as RestartIcon,
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../../utils/api';

const AdminActionsTab = ({ onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', message: '' });

  const handleClearFailed = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.clearFailedJobs();
      setDialogContent({
        title: '✅ Success',
        message: res.data.message,
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to clear failed jobs',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.retryFailedJobs();
      setDialogContent({
        title: '✅ Success',
        message: res.data.message,
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to retry failed jobs',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateKTC = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.updateKTC();
      setDialogContent({
        title: '✅ KTC Update Complete',
        message: res.data.message || `Updated ${res.data.stats?.created || 0} players`,
      });
      setDialogOpen(true);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: error.response?.data?.error || 'Failed to update KTC values',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSleeperPlayers = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.updateSleeperPlayers();
      const stats = res.data.stats || {};
      setDialogContent({
        title: '✅ Sleeper Players Updated',
        message: `Created: ${stats.created || 0}, Updated: ${stats.updated || 0}, Skipped: ${stats.skipped || 0}`,
      });
      setDialogOpen(true);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: error.response?.data?.error || 'Failed to update Sleeper players',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleFlushQueue = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.flushQueue();
      setDialogContent({
        title: '✅ Queue Flushed',
        message: res.data.message,
      });
      setDialogOpen(true);
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      setDialogContent({
        title: '❌ Error',
        message: 'Failed to flush queue',
      });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const actions = [
    {
      title: 'Refresh Dashboard',
      description: 'Reload all metrics and data',
      icon: <RefreshIcon />,
      color: '#3B82F6',
      action: onRefresh,
      enabled: true,
    },
    {
      title: 'Clear Failed Jobs',
      description: 'Remove all failed queue jobs',
      icon: <DeleteIcon />,
      color: '#EF4444',
      action: handleClearFailed,
      enabled: true,
    },
    {
      title: 'Retry Failed Jobs',
      description: 'Retry all failed jobs in the queue',
      icon: <RestartIcon />,
      color: '#F59E0B',
      action: handleRetryFailed,
      enabled: true,
    },
    {
      title: 'Export Metrics',
      description: 'Download CSV of all metrics (Coming Soon)',
      icon: <DownloadIcon />,
      color: '#10B981',
      action: () => {
        setDialogContent({
          title: 'Coming Soon',
          message: 'Metrics export will be available in a future update',
        });
        setDialogOpen(true);
      },
      enabled: true,
    },
    {
      title: 'Update KTC Values',
      description: 'Fetch latest Keep Trade Cut player rankings',
      icon: <TrendingUpIcon />,
      color: '#8B5CF6',
      action: handleUpdateKTC,
      enabled: true,
    },
    {
      title: 'Update Sleeper Players',
      description: 'Sync NFL player data from Sleeper API',
      icon: <PeopleIcon />,
      color: '#06B6D4',
      action: handleUpdateSleeperPlayers,
      enabled: true,
    },
    {
      title: 'Flush Queue',
      description: 'Remove ALL jobs from queue (use with caution)',
      icon: <DeleteSweepIcon />,
      color: '#DC2626',
      action: handleFlushQueue,
      enabled: true,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '20px', fontWeight: 700, marginBottom: '8px', color: '#F1F5F9' }}>
          ⚡ Quick Actions
        </Box>
        <Box sx={{ fontSize: '14px', color: '#94A3B8', marginBottom: '24px' }}>
          Administrative tools for managing your application
        </Box>

        <Grid container spacing={3}>
          {actions.map((action, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Box
                sx={{
                  background: '#0F172A',
                  border: `2px solid ${action.color}`,
                  borderRadius: '12px',
                  padding: '24px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${action.color}44`,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '16px',
                  }}
                >
                  <Box sx={{ color: action.color, fontSize: '32px' }}>
                    {React.cloneElement(action.icon, { sx: { fontSize: '32px' } })}
                  </Box>
                  <Box sx={{ fontSize: '18px', fontWeight: 700, color: '#F1F5F9' }}>
                    {action.title}
                  </Box>
                </Box>

                <Box sx={{ fontSize: '14px', color: '#94A3B8', marginBottom: '20px' }}>
                  {action.description}
                </Box>

                <Button
                  variant="contained"
                  onClick={action.action}
                  disabled={loading || !action.enabled}
                  startIcon={loading ? <CircularProgress size={16} sx={{ color: '#FFF' }} /> : null}
                  sx={{
                    background: action.color,
                    color: '#FFF',
                    fontWeight: 700,
                    padding: '12px 24px',
                    borderRadius: '8px',
                    textTransform: 'none',
                    '&:hover': {
                      background: action.color,
                      opacity: 0.9,
                    },
                    '&:disabled': {
                      background: '#374151',
                      color: '#6B7280',
                    },
                  }}
                >
                  {loading ? 'Processing...' : 'Execute'}
                </Button>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '16px', color: '#8B5CF6' }}>
          🚀 Planned Features
        </Box>
        <Box sx={{ fontSize: '14px', color: '#94A3B8', lineHeight: 2 }}>
          ✓ Manual sync trigger for Manager Intel profiles
          <br />
          ✓ User management (ban, delete, reset password)
          <br />
          ✓ Bulk orphan team operations
          <br />
          ✓ Database backup and restore
          <br />
          ✓ Email notification settings
          <br />
          ✓ Feature flag toggles
          <br />
          ✓ API rate limit configuration
          <br />
          ✓ System log viewer
        </Box>
      </Box>

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1E293B',
            border: '2px solid #334155',
            color: '#F1F5F9',
            minWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ color: '#F1F5F9', fontWeight: 700, fontSize: '20px' }}>
          {dialogContent.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#94A3B8', fontSize: '16px', paddingTop: '8px' }}>
            {dialogContent.message}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              background: '#3B82F6',
              color: '#FFF',
              fontWeight: 700,
              padding: '8px 24px',
              '&:hover': {
                background: '#2563EB',
              },
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminActionsTab;
